'use client';

import type { Easing } from 'motion/react';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { animate, motion, motionValue, useMotionValue, useMotionValueEvent } from 'motion/react';

import type { Direction } from './grading';
import { bezier, easingFor, graphTransition, rollStart } from './grading';
import {
  AXIS,
  DELAY_CURVE,
  DUR_CURVE,
  DUR_NUMBER,
  EASE_GRAPH,
  EASE_SETTLE,
  MORPH_POINTS,
  MORPH_SMOOTH,
  MOUNT_AT,
  SWITCH_AT,
} from './tokens';
import { useMotionReduced } from './use-motion-reduced';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const PAINT_INTERVAL_MS = 33;

type DrawInProps = {
  /** `false` renders the resting state with no animation. */
  readonly active?: boolean;
  /** How much is already revealed on the first frame. */
  readonly from?: number;
  readonly delay?: number;
  readonly duration?: number;
  readonly ease?: Easing;
  /** A CSS length for the reveal's rounded leading edge, for a bar rather than a chart. */
  readonly round?: string;
  readonly className?: string;
  readonly children: ReactNode;
};

/**
 * A left-to-right wipe for a chart's first appearance. The curve is already drawn at its true shape; this
 * reveals the last stretch of it, so what reads is the line travelling forward rather than the plot inflating
 * from a flat baseline. Clip-path only, so it costs no React renders and no Recharts work.
 */
function DrawIn({
  active = true,
  from = MOUNT_AT,
  delay = DELAY_CURVE,
  duration = DUR_CURVE,
  ease,
  round,
  className,
  children,
}: DrawInProps) {
  const reduced = useMotionReduced();
  const ref = useRef<HTMLDivElement>(null);
  const rest = !active || reduced;
  const hidden = Math.max(0, Math.min(100, (1 - from) * 100));
  const clip = (pct: number) => `inset(0 ${pct}% 0 0${round === undefined ? '' : ` round ${round}`})`;
  return (
    <motion.div
      ref={ref}
      data-slot="draw-in"
      className={className}
      initial={rest ? false : { clipPath: clip(hidden) }}
      animate={rest ? undefined : { clipPath: clip(0) }}
      transition={{ duration, delay, ease: ease ?? bezier(EASE_GRAPH) }}
      // A finished clip still clips: a tooltip pinned at the plot's edge would lose its shadow.
      onAnimationComplete={() => ref.current?.style.removeProperty('clip-path')}
    >
      {children}
    </motion.div>
  );
}

type FadeInProps = {
  readonly delay?: number;
  readonly y?: number;
  readonly duration?: number;
  readonly className?: string;
  readonly children: ReactNode;
};

/** A plain entrance, fade up and settle: supporting stats are read, not celebrated. */
function FadeIn({ delay = 0, y = 6, duration = 0.32, className, children }: FadeInProps) {
  const reduced = useMotionReduced();
  if (reduced) return <span className={cn('inline-block', className)}>{children}</span>;
  return (
    <motion.span
      data-slot="fade-in"
      className={cn('inline-block', className)}
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration, delay, ease: EASE_SETTLE }}
    >
      {children}
    </motion.span>
  );
}

type UseAnimatedValueOptions = {
  readonly duration?: number;
  /** Open at this fraction of the target; a reveal paints at its opening value, never the final one. */
  readonly startAt?: number;
  readonly delay?: number;
  readonly direction?: Direction;
  readonly ease?: Easing;
  readonly live?: boolean;
};

/**
 * A plain number that travels to `target`, for a chart prop such as a ring's sweep. Re-renders its owner on every
 * frame, so prefer `AnimatedNumber` for text.
 */
function useAnimatedValue(
  target: number,
  { duration = DUR_NUMBER, startAt, delay = 0, direction = 'neutral', ease, live = true }: UseAnimatedValueOptions = {},
): number {
  const safe = Number.isFinite(target) ? target : 0;
  const reduced = useMotionReduced();
  const [shown, setShown] = useState(startAt === undefined ? safe : safe * startAt);

  // Keyed on the curve's values, not the array's identity: callers pass `bezier(EASE_GRAPH)` or a literal, which
  // is a fresh array every render. As a raw dep that restarts the animation each frame and pins the value at `from`.
  const easeRef = useRef(ease);
  easeRef.current = ease;
  const easeKey = Array.isArray(ease) ? ease.join(',') : String(ease);

  useEffect(() => {
    if (!live || reduced) {
      setShown(safe);
      return;
    }
    const from = startAt === undefined ? rollStart(safe) : safe * startAt;
    if (startAt !== undefined) setShown(from);
    const value = motionValue(from);
    // Throttled to the same ~30fps as `useMorphSeries`: every repaint re-renders this hook's owner, and for a
    // Recharts ring that rebuilds the whole SVG arc set. Half the frames still read as continuous.
    let lastPaint = 0;
    const unsub = value.on('change', (current) => {
      const now = performance.now();
      if (now - lastPaint < PAINT_INTERVAL_MS) return;
      lastPaint = now;
      setShown(current);
    });
    const controls = animate(value, safe, {
      duration,
      delay,
      ease: easeRef.current ?? easingFor(direction),
      // The last frame the driver emits is rounded off the curve; a ring's sweep has to close on the exact target
      // or it stops a hair short of full.
      onComplete: () => setShown(safe),
    });
    return () => {
      controls.stop();
      unsub();
    };
  }, [safe, duration, delay, direction, easeKey, live, reduced, startAt]);

  return shown;
}

/**
 * Briefly dims whenever `dep` changes, before paint, so swapped axis labels are never seen switching in place:
 * they ease out, change while invisible, and ease back in while the curve flows underneath.
 */
function useFadeOnChange(dep: unknown, ms: number = AXIS.out): boolean {
  const [dim, setDim] = useState(false);
  const reduced = useMotionReduced();
  const first = useRef(true);
  useIsomorphicLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (reduced) return;
    setDim(true);
    const timer = setTimeout(() => setDim(false), ms);
    return () => clearTimeout(timer);
  }, [dep, ms, reduced]);
  return dim;
}

type Sample = number | null;

/** Linear resample to `n` points by normalised position; a gap stays a gap. */
function resample(values: readonly Sample[], n: number): Sample[] {
  if (n <= 0) return [];
  if (values.length === 0) return Array.from({ length: n }, () => null);
  if (values.length === 1) return Array.from({ length: n }, () => values[0] ?? null);
  if (values.length === n) return [...values];
  const span = values.length - 1;
  return Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0 : (i / (n - 1)) * span;
    const lo = Math.floor(t);
    const hi = Math.min(lo + 1, span);
    const a = values[lo] ?? null;
    const b = values[hi] ?? null;
    if (a === null || b === null) return t - lo < 0.5 ? a : b;
    return a + (b - a) * (t - lo);
  });
}

type MorphRow = Readonly<Record<string, unknown>>;

type Samples = Readonly<Record<string, Sample>>;

function sampleAt(row: MorphRow, key: string): Sample {
  const value = row[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function withSamples<R extends MorphRow>(row: R, samples: Samples): R {
  return { ...row, ...samples };
}

function samplesFor(keys: readonly string[], sample: (key: string) => Sample): Samples {
  return Object.fromEntries(keys.map((key) => [key, sample(key)]));
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Rebuild `rows` as `n` evenly spaced rows. Downsampling AVERAGES each vertex over the source points it spans,
 * because point-sampling a daily series draws whichever day lands on a sample: that is aliasing, and it was the
 * jitter. Upsampling interpolates. Non-numeric fields come from the nearest real row so labels stay sensible
 * mid-flight, and the endpoints are held exactly because the headline is computed from them.
 */
function resampleRows<R extends MorphRow>(rows: readonly R[], keys: readonly string[], n: number): R[] {
  const first = rows[0];
  if (first === undefined || rows.length === n) return [...rows];
  const span = rows.length - 1;
  const bucket = span / Math.max(1, n - 1);
  const averaging = rows.length > n && bucket > 1.5;
  const out = Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0 : (i / (n - 1)) * span;
    const lo = Math.floor(t);
    const hi = Math.min(lo + 1, span);
    const f = t - lo;
    const base = rows[f < 0.5 ? lo : hi] ?? first;
    return withSamples(
      base,
      samplesFor(keys, (key) => {
        if (averaging) {
          const from = Math.max(0, Math.round(t - bucket / 2));
          const to = Math.min(span, Math.round(t + bucket / 2));
          const found = rows.slice(from, to + 1).flatMap((row) => {
            const v = sampleAt(row, key);
            return v === null ? [] : [v];
          });
          return found.length === 0 ? sampleAt(base, key) : mean(found);
        }
        const a = sampleAt(rows[lo] ?? base, key);
        const b = sampleAt(rows[hi] ?? base, key);
        return a === null || b === null ? sampleAt(base, key) : a + (b - a) * f;
      }),
    );
  });
  if (MORPH_SMOOTH > 0 && out.length > 4) {
    for (const key of keys) {
      const src = out.map((row) => sampleAt(row, key));
      const finite = src.flatMap((v) => (v === null ? [] : [v]));
      if (finite.length !== src.length) continue;
      for (let i = 1; i < out.length - 1; i++) {
        const from = Math.max(0, i - MORPH_SMOOTH);
        const to = Math.min(out.length - 1, i + MORPH_SMOOTH);
        const row = out[i];
        if (row !== undefined) out[i] = withSamples(row, { [key]: mean(finite.slice(from, to + 1)) });
      }
    }
  }
  return out;
}

type Shape = Readonly<Record<string, readonly Sample[]>>;

function shapeOf(rows: readonly MorphRow[], keys: readonly string[]): Shape {
  return Object.fromEntries(keys.map((key) => [key, rows.map((row) => sampleAt(row, key))]));
}

function sameShape(a: Shape | null, b: Shape, keys: readonly string[]): boolean {
  if (a === null) return false;
  return keys.every((key) => {
    const left = a[key] ?? [];
    const right = b[key] ?? [];
    return left.length === right.length && left.every((v, i) => v === right[i]);
  });
}

type Leg<R> = {
  readonly from: Shape;
  readonly to: Shape;
  readonly rows: readonly R[];
};

type UseMorphSeriesOptions = {
  readonly duration?: number;
  readonly switchAt?: number;
};

/**
 * A frame of `data` whose numeric `keys` are interpolated toward their real values. On mount the curve is
 * revealed by `DrawIn`, so nothing interpolates; on a change of data it travels from the shape last drawn to
 * the new one along the graph curve, at a fixed vertex count, then lands on the exact data. Non-numeric fields
 * always come from the target rows, so axes and tooltips switch at once while only the curve morphs.
 */
function useMorphSeries<R extends MorphRow>(
  data: readonly R[],
  keys: readonly (keyof R & string)[],
  { duration = DUR_CURVE, switchAt = SWITCH_AT }: UseMorphSeriesOptions = {},
): readonly R[] {
  const keySignature = keys.join('|');
  const keyList = useMemo(() => keySignature.split('|').filter((k) => k !== ''), [keySignature]);
  const reduced = useMotionReduced();
  const [frame, setFrame] = useState<readonly R[]>(data);
  const shownRef = useRef<Shape | null>(null);
  const legRef = useRef<Leg<R> | null>(null);
  const progress = useMotionValue(1);
  const lastPaintRef = useRef(0);

  useMotionValueEvent(progress, 'change', (p) => {
    const leg = legRef.current;
    if (leg === null) return;
    // Every repaint re-renders the whole chart through React; ~30fps still reads as continuous at half the work.
    const now = performance.now();
    if (p < 1 && now - lastPaintRef.current < PAINT_INTERVAL_MS) return;
    lastPaintRef.current = now;
    const painted: Shape = Object.fromEntries(
      keyList.map((key) => [
        key,
        (leg.to[key] ?? []).map((to, i) => {
          const from = leg.from[key]?.[i] ?? null;
          return to === null || from === null ? to : from + (to - from) * p;
        }),
      ]),
    );
    shownRef.current = painted;
    setFrame(
      leg.rows.map((row, i) =>
        withSamples(
          row,
          samplesFor(keyList, (key) => painted[key]?.[i] ?? null),
        ),
      ),
    );
  });

  useEffect(() => {
    const targets = shapeOf(data, keyList);
    const land = () => {
      legRef.current = null;
      shownRef.current = targets;
      setFrame(data);
    };
    const shown = shownRef.current;
    if (data.length === 0 || reduced || shown === null || sameShape(shown, targets, keyList)) {
      land();
      return;
    }
    const rows = resampleRows(data, keyList, MORPH_POINTS);
    const to = shapeOf(rows, keyList);
    const from: Shape = Object.fromEntries(keyList.map((key) => [key, resample(shown[key] ?? [], rows.length)]));
    legRef.current = { from, to, rows };
    progress.set(switchAt);
    const controls = animate(progress, 1, { ...graphTransition(duration), onComplete: land });
    return () => controls.stop();
  }, [data, keyList, duration, switchAt, reduced, progress]);

  return frame;
}

export { DrawIn, FadeIn, resample, resampleRows, useAnimatedValue, useFadeOnChange, useMorphSeries };
export type { DrawInProps, FadeInProps, MorphRow, Sample, UseAnimatedValueOptions, UseMorphSeriesOptions };
